import React, { useState, useEffect } from 'react'
import '../style/PartnerSettings.css'
import { host } from '../script/variables'
import PartnerPlans from './PartnerPlans'
import { OPEN_PLANS_EVENT } from './planGuard'

const PartnerSettings = ({ initialTab }) => {
    const token = localStorage.getItem('partnertoken')

    const [activeTab, setActiveTab] = useState(initialTab || 'profile')

    useEffect(() => { if (initialTab) setActiveTab(initialTab) }, [initialTab])
    useEffect(() => {
        const open = () => setActiveTab('plans')
        window.addEventListener(OPEN_PLANS_EVENT, open)
        return () => window.removeEventListener(OPEN_PLANS_EVENT, open)
    }, [])
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState({ type: '', text: '' })

    const [profileForm, setProfileForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        gender: '',
        dob: '',
        image: '',
    })

    const [socialForm, setSocialForm] = useState({
        insta: '',
        linkedin: '',
        twitter: '',
    })

    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    })

    const [accountForm, setAccountForm] = useState({
        accounttype: 'public',
    })

    useEffect(() => {
        fetchProfile()
    }, [])

    const fetchProfile = async () => {
        try {
            const res = await fetch(`${host}/partner/profile`, {
                headers: { Authorization: `${token}` },
            })
            const data = await res.json()
            if (data.success) {
                const u = data.user
                setProfileForm({
                    firstName: u.firstName || '',
                    lastName: u.lastName || '',
                    email: u.email || '',
                    phone: u.phone || '',
                    gender: u.gender || '',
                    dob: u.dob ? u.dob.substring(0, 10) : '',
                    image: u.image || '',
                })
                setSocialForm({
                    insta: u.insta || '',
                    linkedin: u.linkedin || '',
                    twitter: u.twitter || '',
                })
                setAccountForm({ accounttype: u.accounttype || 'public' })
            }
        } catch (err) {
            showMessage('error', 'Failed to load profile.')
        }
    }

    const showMessage = (type, text) => {
        setMessage({ type, text })
        setTimeout(() => setMessage({ type: '', text: '' }), 4000)
    }

    const handleProfileChange = (e) =>
        setProfileForm({ ...profileForm, [e.target.name]: e.target.value })

    const handleSocialChange = (e) =>
        setSocialForm({ ...socialForm, [e.target.name]: e.target.value })

    const handlePasswordChange = (e) =>
        setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value })

    const handleAccountChange = (e) =>
        setAccountForm({ ...accountForm, [e.target.name]: e.target.value })

    const submitProfile = async (e) => {
        e.preventDefault()
        setLoading(true)
        try {
            const res = await fetch(`${host}/partner/settings/profile`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `${token}`,
                },
                body: JSON.stringify(profileForm),
            })
            const data = await res.json()
            showMessage(data.success ? 'success' : 'error', data.message)
        } catch {
            showMessage('error', 'Something went wrong.')
        }
        setLoading(false)
    }

    const submitSocial = async (e) => {
        e.preventDefault()
        setLoading(true)
        try {
            const res = await fetch(`${host}/partner/settings/social`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `${token}`,
                },
                body: JSON.stringify(socialForm),
            })
            const data = await res.json()
            showMessage(data.success ? 'success' : 'error', data.message)
        } catch {
            showMessage('error', 'Something went wrong.')
        }
        setLoading(false)
    }

    const submitPassword = async (e) => {
        e.preventDefault()
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            return showMessage('error', 'New passwords do not match.')
        }
        if (passwordForm.newPassword.length < 6) {
            return showMessage('error', 'Password must be at least 6 characters.')
        }
        setLoading(true)
        try {
            const res = await fetch(`${host}/partner/settings/password`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `${token}`,
                },
                body: JSON.stringify({
                    currentPassword: passwordForm.currentPassword,
                    newPassword: passwordForm.newPassword,
                }),
            })
            const data = await res.json()
            showMessage(data.success ? 'success' : 'error', data.message)
            if (data.success) setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
        } catch {
            showMessage('error', 'Something went wrong.')
        }
        setLoading(false)
    }

    const submitAccount = async (e) => {
        e.preventDefault()
        setLoading(true)
        try {
            const res = await fetch(`${host}/partner/settings/account`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `${token}`,
                },
                body: JSON.stringify(accountForm),
            })
            const data = await res.json()
            showMessage(data.success ? 'success' : 'error', data.message)
        } catch {
            showMessage('error', 'Something went wrong.')
        }
        setLoading(false)
    }

    const tabs = [
        { id: 'profile', label: 'Profile', icon: '👤' },
        { id: 'social', label: 'Social Links', icon: '🔗' },
        { id: 'password', label: 'Password', icon: '🔒' },
        { id: 'account', label: 'Account', icon: '⚙️' },
        { id: 'plans', label: 'Plans & Billing', icon: '💳' },
    ]

    return (
        <div className="ps-wrapper">
            <div className={`ps-container ${activeTab === 'plans' ? 'ps-container--wide' : ''}`}>
                {/* Header */}
                <div className="ps-header">
                    <div className="ps-header-left">
                        <span className="ps-badge">Restaurant Owner</span>
                        <h1 className="ps-title">Settings</h1>
                        <p className="ps-subtitle">Manage your restaurant account preferences</p>
                    </div>
                    <div className="ps-avatar">
                        {profileForm.image && profileForm.image !== '#'
                            ? <img src={profileForm.image} alt="avatar" />
                            : <span>{profileForm.firstName?.charAt(0) || '?'}</span>}
                    </div>
                </div>

                {/* Toast Message */}
                {message.text && (
                    <div className={`ps-toast ps-toast--${message.type}`}>
                        <span className="ps-toast-icon">{message.type === 'success' ? '✓' : '✕'}</span>
                        {message.text}
                    </div>
                )}

                <div className="ps-body">
                    {/* Sidebar Tabs */}
                    <nav className="ps-sidebar">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                className={`ps-tab ${activeTab === tab.id ? 'ps-tab--active' : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                <span className="ps-tab-icon">{tab.icon}</span>
                                <span className="ps-tab-label">{tab.label}</span>
                                {activeTab === tab.id && <span className="ps-tab-indicator" />}
                            </button>
                        ))}
                    </nav>

                    {/* Content Panels */}
                    <div className="ps-panel">

                        {/* Profile Tab */}
                        {activeTab === 'profile' && (
                            <form className="ps-form" onSubmit={submitProfile}>
                                <div className="ps-form-header">
                                    <h2>Personal Information</h2>
                                    <p>Update your name, contact, and basic details</p>
                                </div>

                                <div className="ps-grid ps-grid--2">
                                    <div className="ps-field">
                                        <label>First Name</label>
                                        <input
                                            type="text"
                                            name="firstName"
                                            value={profileForm.firstName}
                                            onChange={handleProfileChange}
                                            placeholder="Enter first name"
                                            required
                                        />
                                    </div>
                                    <div className="ps-field">
                                        <label>Last Name</label>
                                        <input
                                            type="text"
                                            name="lastName"
                                            value={profileForm.lastName}
                                            onChange={handleProfileChange}
                                            placeholder="Enter last name"
                                        />
                                    </div>
                                </div>

                                <div className="ps-field">
                                    <label>Email Address</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={profileForm.email}
                                        onChange={handleProfileChange}
                                        placeholder="you@example.com"
                                        required
                                    />
                                </div>

                                <div className="ps-grid ps-grid--2">
                                    <div className="ps-field">
                                        <label>Phone Number</label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={profileForm.phone}
                                            onChange={handleProfileChange}
                                            placeholder="+91 XXXXX XXXXX"
                                        />
                                    </div>
                                    <div className="ps-field">
                                        <label>Gender</label>
                                        <select name="gender" value={profileForm.gender} onChange={handleProfileChange}>
                                            <option value="">Select gender</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                            <option value="other">Other</option>
                                            <option value="prefer_not_to_say">Prefer not to say</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="ps-grid ps-grid--2">
                                    <div className="ps-field">
                                        <label>Date of Birth</label>
                                        <input
                                            type="date"
                                            name="dob"
                                            value={profileForm.dob}
                                            onChange={handleProfileChange}
                                        />
                                    </div>
                                    {/* <div className="ps-field">
                                        <label>Profile Image URL</label>
                                        <input
                                            type="url"
                                            name="image"
                                            value={profileForm.image}
                                            onChange={handleProfileChange}
                                            placeholder="https://..."
                                        />
                                    </div> */}
                                </div>

                                <div className="ps-actions">
                                    <button type="submit" className="ps-btn ps-btn--primary" disabled={loading}>
                                        {loading ? <span className="ps-spinner" /> : 'Save Changes'}
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* Social Tab */}
                        {activeTab === 'social' && (
                            <form className="ps-form" onSubmit={submitSocial}>
                                <div className="ps-form-header">
                                    <h2>Social Links</h2>
                                    <p>Connect your social media profiles</p>
                                </div>

                                {[
                                    { name: 'insta', label: 'Instagram', placeholder: 'https://instagram.com/yourhandle', emoji: '📸' },
                                    { name: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/yourprofile', emoji: '💼' },
                                    { name: 'twitter', label: 'Twitter / X', placeholder: 'https://twitter.com/yourhandle', emoji: '🐦' },
                                ].map(({ name, label, placeholder, emoji }) => (
                                    <div className="ps-field ps-field--icon" key={name}>
                                        <label>{label}</label>
                                        <div className="ps-input-wrap">
                                            <span className="ps-input-emoji">{emoji}</span>
                                            <input
                                                type="url"
                                                name={name}
                                                value={socialForm[name]}
                                                onChange={handleSocialChange}
                                                placeholder={placeholder}
                                            />
                                        </div>
                                    </div>
                                ))}

                                <div className="ps-actions">
                                    <button type="submit" className="ps-btn ps-btn--primary" disabled={loading}>
                                        {loading ? <span className="ps-spinner" /> : 'Update Links'}
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* Password Tab */}
                        {activeTab === 'password' && (
                            <form className="ps-form" onSubmit={submitPassword}>
                                <div className="ps-form-header">
                                    <h2>Change Password</h2>
                                    <p>Use a strong password of at least 6 characters</p>
                                </div>

                                <div className="ps-field">
                                    <label>Current Password</label>
                                    <input
                                        type="password"
                                        name="currentPassword"
                                        value={passwordForm.currentPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Enter current password"
                                        required
                                    />
                                </div>

                                <div className="ps-field">
                                    <label>New Password</label>
                                    <input
                                        type="password"
                                        name="newPassword"
                                        value={passwordForm.newPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Enter new password"
                                        required
                                    />
                                </div>

                                <div className="ps-field">
                                    <label>Confirm New Password</label>
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        value={passwordForm.confirmPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Re-enter new password"
                                        required
                                    />
                                    {passwordForm.confirmPassword &&
                                        passwordForm.newPassword !== passwordForm.confirmPassword && (
                                            <span className="ps-field-error">Passwords don't match</span>
                                        )}
                                </div>

                                <div className="ps-password-rules">
                                    {['At least 6 characters', 'Mix of letters and numbers recommended', 'Avoid using personal info'].map(rule => (
                                        <div key={rule} className="ps-rule">
                                            <span className="ps-rule-dot" />
                                            {rule}
                                        </div>
                                    ))}
                                </div>

                                <div className="ps-actions">
                                    <button type="submit" className="ps-btn ps-btn--primary" disabled={loading}>
                                        {loading ? <span className="ps-spinner" /> : 'Update Password'}
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* Account Tab */}
                        {activeTab === 'account' && (
                            <form className="ps-form" onSubmit={submitAccount}>
                                <div className="ps-form-header">
                                    <h2>Account Visibility</h2>
                                    <p>Control who can see your restaurant profile</p>
                                </div>

                                <div className="ps-field">
                                    <label>Account Type</label>
                                    <div className="ps-radio-group">
                                        {[
                                            { value: 'public', label: 'Public', desc: 'Visible to everyone', icon: '🌐' },
                                            { value: 'private', label: 'Private', desc: 'Only visible to approved followers', icon: '🔒' },
                                            { value: 'protected', label: 'Protected', desc: 'Visible to logged-in users', icon: '🛡️' },
                                        ].map(opt => (
                                            <label
                                                key={opt.value}
                                                className={`ps-radio-card ${accountForm.accounttype === opt.value ? 'ps-radio-card--active' : ''}`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="accounttype"
                                                    value={opt.value}
                                                    checked={accountForm.accounttype === opt.value}
                                                    onChange={handleAccountChange}
                                                />
                                                <span className="ps-radio-icon">{opt.icon}</span>
                                                <div>
                                                    <strong>{opt.label}</strong>
                                                    <p>{opt.desc}</p>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="ps-actions">
                                    <button type="submit" className="ps-btn ps-btn--primary" disabled={loading}>
                                        {loading ? <span className="ps-spinner" /> : 'Save Preferences'}
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* Plans & Billing Tab */}
                        {activeTab === 'plans' && <PartnerPlans />}

                    </div>
                </div>
            </div>
        </div>
    )
}

export default PartnerSettings